import {
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
  AfterViewInit,
  inject,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgClass } from '@angular/common';
import { EditorComponent, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { Exception, ToasterService } from '@anddone/ui-utilities';
import { Notification } from '../../../../../services/notification';
import { APIConstants } from '../../../../../constants/api.constant';
import { MessageConstants } from '../../../../../constants/message.constant';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { Loader } from '../../../../layout/loader/loader';
import { forkJoin } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-notification-template',
  imports: [NgClass, EditorComponent, Loader, TranslateModule, FormsModule],
  templateUrl: './notification-template.html',
  styleUrl: './notification-template.scss',
  providers: [{ provide: TINYMCE_SCRIPT_SRC, useValue: '/tinymce/tinymce.min.js' }],
})
export class NotificationTemplate implements OnInit, AfterViewInit {
  @Input() notificationId!: string;
  @ViewChild('editorContainer', { static: false }) editorContainer!: ElementRef;
  loggedInUserData: any;
  postAPICallForSaveTemplate: any;
  putAPICallForEditTemplate: any;
  previewTemplateSubject: any;
  updateTemplateSubject: any;
  eventVariables: any;
  textEditorTemplates: any;
  editNotificationTemplate: any;
  editorReference: any;
  plainTextToggle: any;
  styledToggle: any;
  isLoader = signal<boolean>(true);
  editorConfig = {};
  isPreviewActive = true;
  eventName: any;
  notificationEvent: any;
  notificationTemplateFlag = signal<boolean>(true);
  resetToDefaultFlag = signal<boolean>(false);
  saveTemplateFlag = signal<boolean>(false);
  editTemplateFlag = signal<boolean>(false);
  defaultTemplatePreview = signal<boolean>(false);
  templateVariablesList: any = [];

  public activeModal = inject(NgbActiveModal);
  private r2 = inject(Renderer2);
  private toasterService = inject(ToasterService);
  private notificationService = inject(Notification);
  private allyCommonService = inject(AllyCommonService);

  ngOnInit(): void {
    this.loggedInUserData = this.allyCommonService.getLoggedInData() || '';
    this.notificationTemplateAction();
    this.getEventDetails(this.notificationId);
  }

  ngAfterViewInit() {
    this.initPlainTextToggles();
  }

  close() {
    this.activeModal.close('Modal closed');
  }

  toggleTemplateTab(isPreview: boolean): void {
    this.isPreviewActive = isPreview;
    this.previewTemplateSubject = this.updateTemplateSubject;
    if (isPreview) {
      this.eventVariables?.forEach((variable: any) => {
        const pattern = new RegExp(`\\[\\[${variable?.displayName}\\]\\]`, 'g');
        if (this.previewTemplateSubject?.includes(`[[${variable.displayName}]]`)) {
          this.previewTemplateSubject = this.previewTemplateSubject?.replace(
            pattern,
            variable.sampleData || '--'
          );
        }
      });
    }

    if (!isPreview && this.editorReference) {
      this.editNotificationTemplate = this.textEditorTemplates;
      this.editorReference?.setContent(this.editNotificationTemplate);
      this.eventVariables?.forEach((variable: any) => {
        if (variable.sampleData && variable.sampleData !== '') {
          const pattern = new RegExp(`\\[\\[${variable.displayName}\\]\\]`, 'g');
          if (this.updateTemplateSubject?.match(pattern)) {
            this.updateTemplateSubject = this.updateTemplateSubject?.replace(
              pattern,
              `[[${variable.displayName}]]`
            );
          }
        }
      });
    }
  }

  initPlainTextToggles() {
    this.plainTextToggle = document.getElementById('viewPlainTextLink');
    this.styledToggle = document.getElementById('viewStyledLink');
    this.plainTextToggle.addEventListener('click', this.plainTextToggleEvent);
    this.styledToggle.addEventListener('click', this.styledTextToggleEvent);
  }

  plainTextToggleEvent() {
    const plainTextToggle = document.getElementById('viewPlainTextLink');
    const styledToggle = document.getElementById('viewStyledLink');
    const plainTarget = document.getElementById(
      plainTextToggle?.dataset['adTarget'] ? plainTextToggle.dataset['adTarget'] : ''
    );
    const styledTarget = document.getElementById(
      styledToggle?.dataset['adTarget'] ? styledToggle.dataset['adTarget'] : ''
    );

    toggleReveal(plainTarget);
    toggleReveal(styledTarget);
    toggleReveal(plainTextToggle);
    toggleReveal(styledToggle);

    function toggleReveal(element: any) {
      if (element.classList?.contains('d-none')) {
        element.classList.remove('d-none');
      } else {
        element.classList?.add('d-none');
      }
    }
  }

  styledTextToggleEvent() {
    const plainTextToggle = document.getElementById('viewPlainTextLink');
    const styledToggle = document.getElementById('viewStyledLink');
    const plainTarget = document.getElementById(
      plainTextToggle?.dataset['adTarget'] ? plainTextToggle.dataset['adTarget'] : ''
    );
    const styledTarget = document.getElementById(
      styledToggle?.dataset['adTarget'] ? styledToggle.dataset['adTarget'] : ''
    );

    toggleReveal(plainTarget);
    toggleReveal(styledTarget);
    toggleReveal(plainTextToggle);
    toggleReveal(styledToggle);

    function toggleReveal(element: any) {
      if (element.classList?.contains('d-none')) {
        element.classList.remove('d-none');
      } else {
        element.classList?.add('d-none');
      }
    }
  }

  notificationTemplateAction() {
    this.toggleActiveState(true);
    this.notificationTemplateFlag.set(true);
    this.editorConfig = {
      base_url: '/tinymce',
      suffix: '.min',
      menubar: false,
      noneditable_class: 'non-editable',
      block_formats: 'Bold Header=h1; Table Header=h2; Table Label=h5; Default=p;',
      font_size_input_default_unit: 'px',
      font_size_formats: '8px 10px 12px 14px 16px 18px 24px 36px 48px',
      toolbar: [
        'undo redo | remove removeformat | styles fontfamily fontsize | bold italic underline strikethrough subscript superscript hr | forecolor backcolor | bullist numlist checklist | link charmap table | template code',
      ],
      style_formats: [
        {
          title: 'Headers',
          items: [
            {
              title: 'Large Header',
              inline: 'span',
              styles: {
                color: 'black',
                fontWeight: '700',
                fontSize: '20px',
                display: 'inline',
                textTransform: 'unset',
              },
            },
            {
              title: 'Table Header',
              inline: 'span',
              styles: {
                color: '#174770',
                fontWeight: '200',
                fontSize: '18px',
                lineHeight: '1.25',
                margin: '0.5rem 0',
                letterSpacing: '1px',
                display: 'block',
                textTransform: 'unset',
              },
            },
          ],
        },
        {
          title: 'Default',
          inline: 'span',
          styles: {
            fontSize: '12px',
            color: '#174770',
            fontWeight: '400',
            lineHeight: '1.4',
            textTransform: 'unset',
          },
        },
        {
          title: 'Label',
          inline: 'span',
          styles: {
            color: '#174770',
            fontWeight: 'bold',
            fontSize: '12px',
            lineHeight: '1',
            textTransform: 'uppercase',
          },
        },
      ],
      toolbar_location: 'top',
      plugins: ['table', 'autoresize', 'template', 'emoticons', 'link', 'image', 'code', 'charmap'],
      min_height: 300,
      init_instance_callback: (editor: any) => {
        this.renameButtonWithTitle('Insert template', 'Insert Template Variables');
        this.editorReference = editor;
        let intervalId: any;
        editor.on('ExecCommand', (event: any) => {
          const command = event.command;
          switch (command) {
            case 'mceTemplate': {
              const dialogTitleElement = document.querySelector('.tox-dialog__title');
              intervalId = setInterval(() => {
                if (dialogTitleElement) {
                  dialogTitleElement.innerHTML = 'Insert Template Variables';
                }
              }, 100);
              break;
            }
            case 'mceFocus':
              clearInterval(intervalId);
              break;
          }
        });
      },
      templates: this.templateVariablesList,
      image_title: true,
      automatic_uploads: true,
      file_picker_types: 'image',
      file_picker_callback: (cb: any, _value: any, _meta: any) => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');

        input.addEventListener('change', (event: Event) => {
          if ((event?.target as HTMLInputElement)?.files) {
            const uploadedFile = (event.target as HTMLInputElement).files || [];
            const target = event.target as HTMLInputElement;
            for (let index = 0; index < uploadedFile.length; index++) {
              const file = target.files![index];
              const reader = new FileReader();
              reader.onload = async (_e) => {
                const id = 'blobid' + new Date().getTime();
                const blobCache = this.editorReference.editorUpload.blobCache;
                const base64 = reader.result as string;
                const blobInfo = blobCache.create(id, file, base64);
                blobCache.add(blobInfo);
                cb(blobInfo.blobUri(), { title: file.name });
              };
              reader.readAsDataURL(file);
            }
          }
        });
        input.click();
      },
    };
  }

  revertToLastSave() {
    this.isLoader.set(true);
    if (this.editorReference) {
      setTimeout(() => {
        this.isLoader.set(false);
        this.editNotificationTemplate = this.textEditorTemplates;
        this.editorReference.setContent(this.editNotificationTemplate);
        this.toasterService.showSuccess(MessageConstants.notification.revertNotification, 'full');
      }, 1000);
    }
  }

  resetContent() {
    this.isLoader.set(true);
    this.notificationService.resetToSystemDefaultTemplate(this.notificationEvent?.id).subscribe({
      next: (_res: any) => {
        this.isLoader.set(false);
        this.getEventDetails(this.notificationEvent?.id, true);
        setTimeout(() => {
          this.toasterService.showSuccess(
            MessageConstants.notification.resetToSystemDefault,
            'full'
          );
        }, 1000);
      },
      error: (e) => {
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  saveContent() {
    const updateNotificationTemplate = this.editorReference
      ?.getContent()
      ?.replace('src="./assets/images/AndDoneLogo.png"', 'src="cid:headerLogo"');
    let htmlTemplate = updateNotificationTemplate;

    this.eventVariables.forEach((item: any) => {
      const placeholder = `[[${item.displayName}]]`;
      const replacement = `[[${item.fieldName}]]`;
      htmlTemplate = htmlTemplate?.split(placeholder).join(replacement);
    });

    if (this.saveTemplateFlag()) {
      const postApiCalls = this.handleAPICallForTemplate(
        'POST',
        this.postAPICallForSaveTemplate,
        htmlTemplate
      );

      this.isLoader.set(true);
      forkJoin(postApiCalls).subscribe({
        next: (_postResponses: any[]) => {
          this.isLoader.set(false);
          this.getEventDetails(this.notificationEvent?.id, true);
          this.toasterService.showSuccess(MessageConstants.notification.saveTemplate, 'full');
        },
        error: (e) => {
          this.isLoader.set(false);
          if (e?.error?.message == 'Key_InvalidOrNonExistentTemplateVariableAddedKindlyRevisit') {
            this.toasterService.showError(
              MessageConstants.notification.invalidTempVariable,
              'full'
            );
          } else if (e?.error?.errors?.length) {
            const toastErrors = [];
            for (let i = 0; i < e?.error?.errors.length; i++) {
              if (e?.error?.errors[i].message.includes('Key_InvalidVariable')) {
                toastErrors.push(e?.error?.errors[i]?.field);
              }
            }
            const toastMessage = `Invalid Variable ${toastErrors.join(', ')}`;
            this.toasterService.showError(toastMessage, 'full');
          } else {
            const toastMessage = Exception.exceptionMessage(e);
            this.toasterService.showError(toastMessage.join(', '), 'full');
          }
        },
      });
    }

    if (this.editTemplateFlag()) {
      const putApiCalls = this.handleAPICallForTemplate(
        'PUT',
        this.putAPICallForEditTemplate,
        htmlTemplate
      );

      this.isLoader.set(true);
      forkJoin(putApiCalls).subscribe({
        next: (_putResponses: any[]) => {
          this.isLoader.set(false);
          this.getEventDetails(this.notificationEvent?.id, true);
          this.toasterService.showSuccess(MessageConstants.notification.saveTemplate, 'full');
        },
        error: (e) => {
          this.isLoader.set(false);
          if (e?.error?.message == 'Key_InvalidOrNonExistentTemplateVariableAddedKindlyRevisit') {
            this.toasterService.showError(
              MessageConstants.notification.invalidTempVariable,
              'full'
            );
          } else if (e?.error?.errors?.length) {
            const toastErrors = [];

            for (let i = 0; i < e?.error?.errors.length; i++) {
              if (e?.error?.errors[i].message.includes('Key_InvalidVariable')) {
                toastErrors.push(e?.error?.errors[i]?.field);
              }
            }
            const toastMessage = `Invalid Variable ${toastErrors.join(', ')}`;
            this.toasterService.showError(toastMessage, 'full');
          } else {
            const toastMessage = Exception.exceptionMessage(e);
            this.toasterService.showError(toastMessage.join(', '), 'full');
          }
        },
      });
    }
  }

  handleAPICallForTemplate(method: string, templateDataArray: any[], htmlTemplate: string) {
    const apiCalls = templateDataArray?.map((templateData) => {
      const isPlainText = templateData.isPlainText;
      const template = isPlainText
        ? this.removeStyles(htmlTemplate)
        : this.addHTMLTagSection(htmlTemplate);

      const tempData = {
        templateData: templateData,
        htmlTemplate: btoa(unescape(encodeURIComponent(template))),
        plainTextFlag: isPlainText,
      };
      return this.updateNotificationEventTemplate(tempData, `${method}-API-Call`);
    });
    return forkJoin(apiCalls);
  }

  updateNotificationEventTemplate(notificationData: any, apiCallType: string) {
    this.isLoader.set(false);
    const obj = {
      notificationId: this.notificationEvent.id,
      templateId: notificationData.templateData.id,
      type: apiCallType,
      data: {
        templateName: notificationData.templateData.name,
        description: notificationData.templateData.description,
        isPlainText: notificationData.plainTextFlag,
        subject: this.getSubjectFieldNameFromEvent(),
        from: notificationData.templateData.from,
        to: notificationData.templateData.to,
        cc: '',
        bcc: '',
        body: notificationData.htmlTemplate,
      },
    };
    return this.notificationService.updateNotificationTemplate(obj);
  }

  getSubjectFieldNameFromEvent() {
    let subjectVariable;
    let subjectWithFieldName = this.updateTemplateSubject;
    const matches = this.updateTemplateSubject.match(/\[\[(.*?)\]\]/g);
    if (matches) {
      matches.forEach((match: any) => {
        const contentInsideBrackets = match.substring(2, match.length - 2);
        const eventFieldAsPerSubject = this.eventVariables.find(
          (ele: any) => ele.displayName === contentInsideBrackets
        );
        if (eventFieldAsPerSubject) {
          const escapedTitle = contentInsideBrackets.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`\\[\\[${escapedTitle}\\]\\]`, 'g');
          subjectWithFieldName = subjectWithFieldName.replace(
            regex,
            `[[${eventFieldAsPerSubject.fieldName}]]`
          );
        }
      });
      subjectVariable = subjectWithFieldName;
    } else {
      subjectVariable = subjectWithFieldName;
    }
    return subjectVariable;
  }

  removeStyles(html: string): string {
    html = html?.replace(/style="[^"]*"/gi, '');
    return html;
  }

  addHTMLTagSection(template: any) {
    this.postAPICallForSaveTemplate = this.notificationEvent.emailTemplates.filter(
      (ele: any) => ele.isPlatformDefault && ele.parentId !== this.loggedInUserData.id
    );
    const defaultStyledTemplatePreview = this.postAPICallForSaveTemplate.filter(
      (ele: any) => !ele.isPlainText
    );
    const defaultTemplate = defaultStyledTemplatePreview[0].templateText;

    // Extract the inline styles of the body tag
    const bodyInlineStyleMatch = defaultTemplate.match(/<body[^>]*style="([^"]*)"[^>]*>/i);
    const bodyInlineStyle = bodyInlineStyleMatch ? bodyInlineStyleMatch[1] : '';

    // Replace the entire content inside the <body> tag while preserving inline styles
    const modifiedHtmlString = defaultTemplate.replace(
      /<body[^>]*>([\s\S]*?)<\/body>/i,
      `<body style="${bodyInlineStyle}">${template}</body>`
    );
    const outputString = modifiedHtmlString.replace(/(<!-- Fonts -->\s*<p>&nbsp;<\/p>\s*)+/g, '');
    return outputString;
  }

  resetLink() {
    if (this.plainTextToggle.classList.contains('d-none')) {
      this.plainTextToggleEvent();
    } else if (!this.styledToggle.classList.contains('d-none')) {
      this.styledTextToggleEvent();
    }
  }

  renameButtonWithTitle(oldTitle: string, newTitle: string) {
    const editorContainerElement = this.editorContainer?.nativeElement;
    const toolbarGroups = editorContainerElement?.querySelectorAll('.tox-toolbar__group');
    toolbarGroups?.forEach((toolbarGroup: Element) => {
      const buttonWithTitle = toolbarGroup?.querySelector(`button[title="${oldTitle}"]`);
      if (buttonWithTitle) {
        this.r2.setAttribute(buttonWithTitle, 'title', newTitle);
      }
    });
  }

  getEventDetails(notificationId: string, callFromEditNotification?: boolean) {
    if (notificationId) {
      this.isLoader.set(true);
      this.notificationService
        .getNotificationEventDetails(this.loggedInUserData?.id, notificationId)
        .subscribe({
          next: (res: any) => {
            this.notificationEvent = res;
            this.resetToDefaultFlag.set(res.emailTemplates.length > 2);
            this.eventName = res.eventName;
            if (!callFromEditNotification) {
              this.getEventVariables(res.id);
            }
            if (callFromEditNotification) {
              this.getUpdatedNotificationTemplate(callFromEditNotification);
              this.isLoader.set(false);
            }
          },
          error: (e: any) => {
            this.isLoader.set(false);
            const toastMessage = Exception.exceptionMessage(e);
            this.toasterService.showError(toastMessage.join(', '), 'full');
          },
        });
    }
  }

  getEventVariables(id: string) {
    const obj = {
      EventId: id,
    };
    this.isLoader.set(true);
    this.notificationService.getEventVariables(obj).subscribe({
      next: (res: any) => {
        this.eventVariables = res;
        if (res?.length) {
          res.forEach((ele: any) => {
            this.templateVariablesList.push({
              title: ele.displayName,
              description: ele.displayName,
              content: `[[${ele.displayName}]]`,
            });
          });
          this.getUpdatedNotificationTemplate(false);
          setTimeout(() => {
            this.isLoader.set(false);
          }, 1000);
        } else {
          this.isLoader.set(false);
        }
      },
      error: (e: any) => {
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  toggleActiveState(isPreview: boolean): void {
    this.isPreviewActive = isPreview;
    this.previewTemplateSubject = this.updateTemplateSubject;
    if (isPreview) {
      this.eventVariables?.forEach((variable: any) => {
        const pattern = new RegExp(`\\[\\[${variable?.displayName}\\]\\]`, 'g');
        if (this.previewTemplateSubject?.includes(`[[${variable.displayName}]]`)) {
          this.previewTemplateSubject = this.previewTemplateSubject?.replace(
            pattern,
            variable.sampleData || '--'
          );
        }
      });
    }

    if (!isPreview && this.editorReference) {
      this.editNotificationTemplate = this.textEditorTemplates;
      this.editorReference?.setContent(this.editNotificationTemplate);
      this.eventVariables?.forEach((variable: any) => {
        if (variable.sampleData && variable.sampleData !== '') {
          const pattern = new RegExp(`\\[\\[${variable.displayName}\\]\\]`, 'g');
          if (this.updateTemplateSubject?.match(pattern)) {
            this.updateTemplateSubject = this.updateTemplateSubject?.replace(
              pattern,
              `[[${variable.displayName}]]`
            );
          }
        }
      });
    }
  }

  getUpdatedNotificationTemplate(callFromEditNotification: any) {
    const merchantId = this.loggedInUserData?.id;
    this.postAPICallForSaveTemplate = this.notificationEvent.emailTemplates.filter(
      (ele: any) => ele.isPlatformDefault && ele.parentId !== merchantId
    );
    this.putAPICallForEditTemplate = this.notificationEvent.emailTemplates.filter(
      (ele: any) => !ele.isPlatformDefault && ele.parentId === merchantId
    );
    this.saveTemplateFlag.set(
      this.postAPICallForSaveTemplate.length > 0 && this.putAPICallForEditTemplate.length === 0
    );

    this.editTemplateFlag.set(this.putAPICallForEditTemplate.length > 0);

    if (this.saveTemplateFlag()) {
      // POST API call
      // default preview for styles template
      const defaultStyledTemplatePreview = this.postAPICallForSaveTemplate.filter(
        (ele: any) => !ele.isPlainText
      );

      // default preview for plaintext template
      const defaultPlainTemplatePreview = this.postAPICallForSaveTemplate.filter(
        (ele: any) => ele.isPlainText
      );
      this.defaultTemplatePreview.set(defaultStyledTemplatePreview[0].isAdminEdited !== true);

      this.notificationTemplateRendering(
        defaultStyledTemplatePreview[0],
        defaultPlainTemplatePreview[0],
        callFromEditNotification
      );
    }

    if (this.editTemplateFlag()) {
      // PUT API call
      this.defaultTemplatePreview.set(false);
      // updated preview for styles template
      const merchantCopyStyledTemplatePreview = this.putAPICallForEditTemplate.filter(
        (ele: any) => !ele.isPlainText
      );

      // updated preview for plaintext template
      const merchantCopyPlainTemplatePreview = this.putAPICallForEditTemplate.filter(
        (ele: any) => ele.isPlainText
      );

      this.notificationTemplateRendering(
        merchantCopyStyledTemplatePreview[0],
        merchantCopyPlainTemplatePreview[0],
        callFromEditNotification
      );
    }
  }

  notificationTemplateRendering(
    styledTemplate: any,
    plainTextTemplate: any,
    callFromEditNotification: boolean
  ) {
    const previewFullDiv = document.getElementById('preview-full');
    const previewPlainTextDiv = document.getElementById('preview-plainText');

    if (callFromEditNotification) {
      this.isPreviewActive = true;
    }

    if (styledTemplate) {
      const { templateText, templateSubject } = styledTemplate;
      let styledTemplateText = templateText.replace(/max-width:\s*800px\s*;/g, '');
      styledTemplateText = templateText.replace(/(<!-- Fonts -->\s*<p>&nbsp;<\/p>\s*)+/g, '');
      styledTemplateText = styledTemplateText.replace(
        APIConstants.replacePathFrom,
        APIConstants.replacePathWith
      );
      let previewSampleData = styledTemplateText;
      this.updateTemplateSubject = templateSubject;
      this.previewTemplateSubject = this.updateTemplateSubject;
      this.eventVariables?.forEach((variable: any) => {
        const pattern = new RegExp(`\\[\\[${variable.displayName}\\]\\]`, 'g');
        if (previewSampleData?.includes(`[[${variable.displayName}]]`)) {
          previewSampleData = previewSampleData?.replace(pattern, variable.sampleData || '--');
        }
        if (this.previewTemplateSubject?.includes(`[[${variable.displayName}]]`)) {
          this.previewTemplateSubject = this.previewTemplateSubject?.replace(
            pattern,
            variable.sampleData || '--'
          );
        }
      });

      // Now styledTemplateText contains the replaced values
      if (!previewFullDiv) return;
      previewFullDiv.innerHTML = previewSampleData;
      this.textEditorTemplates = styledTemplateText;

      if (this.editorReference) {
        this.editNotificationTemplate = this.textEditorTemplates;
        this.editorReference.setContent(this.editNotificationTemplate);
      }
    } else {
      if (!previewFullDiv) return;
      previewFullDiv.style.backgroundColor = 'white';
      previewFullDiv.innerHTML = 'Email template not found.';
    }

    if (plainTextTemplate) {
      if (!previewPlainTextDiv) return;
      previewPlainTextDiv.innerHTML = '';
      const { templateText } = plainTextTemplate;
      let plainTextTemplateText = templateText?.replace(
        APIConstants.replacePathFrom,
        APIConstants.replacePathWith
      );
      this.eventVariables?.forEach((variable: any) => {
        const pattern = new RegExp(`\\[\\[${variable.displayName}\\]\\]`, 'g');
        if (plainTextTemplateText?.includes(`[[${variable.displayName}]]`)) {
          plainTextTemplateText = plainTextTemplateText?.replace(
            pattern,
            variable.sampleData || '--'
          );
        }
        if (this.previewTemplateSubject?.includes(`[[${variable.displayName}]]`)) {
          this.previewTemplateSubject = this.previewTemplateSubject?.replace(
            pattern,
            variable.sampleData || '--'
          );
        }
      });

      previewPlainTextDiv.insertAdjacentHTML('beforeend', plainTextTemplateText);
    } else {
      if (!previewPlainTextDiv) return;
      previewPlainTextDiv.innerHTML = '';
      previewPlainTextDiv.style.backgroundColor = 'white';
      previewPlainTextDiv.innerHTML = 'Email template not found.';
    }

    this.plainTextToggle.addEventListener('click', this.plainTextToggleEvent);
    this.styledToggle.addEventListener('click', this.styledTextToggleEvent);
    this.resetLink();
  }
}
